import { FC } from 'react';
import { UserRole } from './ActionsPanel';
import { FiCalendar, FiUser, FiActivity, FiBox } from 'react-icons/fi';
import { useCompletedActions } from '../../contexts/CompletedActionsContext';

interface RecentActivitySectionProps {
  role: UserRole;
}

const RecentActivitySection: FC<RecentActivitySectionProps> = ({ role }) => {
  // Use the completed actions context
  const { getCompletedActionsByRole } = useCompletedActions();
  
  // Get completed actions for the current role
  const completedActions = getCompletedActionsByRole(role);
  
  if (completedActions.length === 0) {
    return (
      <div className="text-center py-6">
        <p className="text-slate-500">No actions have been completed yet. Actions you take will appear here.</p>
      </div>
    );
  }
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(date);
  };
  
  const getCategoryIcon = (category: string) => {
    switch(category.toLowerCase()) {
      case 'lending':
        return <FiBox className="text-blue-600" />;
      case 'marketing':
        return <FiActivity className="text-purple-600" />;
      case 'member wellness':
      case 'member experience':
        return <FiUser className="text-green-600" />;
      default:
        return <FiActivity className="text-slate-600" />;
    }
  };

  return (
    <div className="space-y-4">
      {completedActions.map(action => (
        <div key={action.id} className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex items-start">
          <div className="bg-slate-100 p-3 rounded-full mr-3">
            {getCategoryIcon(action.category)}
          </div>
          
          <div className="flex-1">
            <h3 className="text-slate-800 font-medium">{action.title}</h3>
            <p className="text-slate-600 text-sm">{action.description}</p>
            
            {action.memberName && (
              <div className="flex items-center mt-2 text-xs text-slate-500">
                <FiUser className="mr-1" />
                <span>{action.memberName}</span>
              </div>
            )}
          </div>
          
          <div className="text-right">
            <div className="text-sm font-medium text-slate-800">
              {action.impact.type === 'financial' ? '$' : ''}
              {action.impact.value.toLocaleString()}
              {action.impact.type === 'percentage' ? '%' : ''}
            </div>
            <div className="text-xs text-slate-500 flex items-center justify-end mt-2">
              <FiCalendar className="mr-1" />
              <span>{formatDate(action.completedAt || '')}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default RecentActivitySection; 